import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Plus, X, Upload } from 'lucide-react';
import {SERVER_URL} from '../../constants'
import axios from 'axios';
const CreateCategory = () => {
  const navigate = useNavigate();
  const [category, setCategory] = useState({
    name: '',
    description: '',
    image: '',
    subcategories: [{ name: '', description: ''}],
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setCategory({ ...category, [name]: value });
  };

  const handleSubcategoryChange = (index, e) => {
    const { name, value } = e.target;
    const updatedSubcategories = [...category.subcategories];
    updatedSubcategories[index] = {
      ...updatedSubcategories[index],
      [name]: value,
    };
    setCategory({ ...category, subcategories: updatedSubcategories });
  };

  const uploadImageToCloudinary = async (file) => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('upload_preset', 'unsigned_preset'); // Replace with your Cloudinary upload preset

    try {
      const response = await fetch('https://api.cloudinary.com/v1_1/dkzwhqhbr/image/upload', {
        method: 'POST',
        body: formData,
      });
      const data = await response.json();
      return data.secure_url; // Return the uploaded image URL
    } catch (error) {
      console.error('Error uploading image:', error);
      return '';
    }
  };

  const handleCategoryImageChange = async (e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = await uploadImageToCloudinary(file);
      setCategory({ ...category, image: imageUrl });
    }
  };

  const handleSubcategoryImageChange = async (index, e) => {
    const file = e.target.files[0];
    if (file) {
      const imageUrl = await uploadImageToCloudinary(file);
      const updatedSubcategories = [...category.subcategories];
      updatedSubcategories[index].image = imageUrl;
      setCategory({ ...category, subcategories: updatedSubcategories });
    }
  };

  const addSubcategory = () => {
    setCategory((prev) => ({
      ...prev,
      subcategories: [...prev.subcategories, { name: '', description: ''}],
    }));
  };

  const removeSubcategory = (index) => {
    setCategory((prev) => ({
      ...prev,
      subcategories: prev.subcategories.filter((_, i) => i !== index),
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
  
    try {
      // Category payload
      const categoryPayload = {
        name: category.name,
        description: category.description,
        imgUrl: category.image,
      };
  
      // Create category
      const categoryResponse = await axios.post(`${SERVER_URL}/api/v1/category`, categoryPayload);
  
      if (categoryResponse.status !== 201) {
        console.error('Failed to create category:', categoryResponse.data.message);
        return;
      }
  
      const createdCategory = categoryResponse.data.data;
      const categoryId = createdCategory._id; // Retrieve the newly created category's ID
  
      // Subcategories payload and creation
      for (const sub of category.subcategories) {
        const subcategoryPayload = {
          name: sub.name,
          description: sub.description,
          categoryId, // Pass the categoryId here
        };
  
        try {
          await axios.post(`${SERVER_URL}/api/v1/subcategory`, subcategoryPayload);
        } catch (subError) {
          console.error('Failed to create subcategory:', subError.response?.data?.message || subError.message);
        }
      }
  
      // Navigate to category list
     
    } catch (error) {
      console.error('Error creating category:', error.response?.data?.message || error.message);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 p-4 sm:p-6 md:p-8">
      <motion.div
        className="bg-white shadow-xl rounded-3xl p-6 sm:p-8 w-full max-w-5xl"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        exit={{ opacity: 0, scale: 0.9 }}
        transition={{ duration: 0.3 }}
      >
        <h2 className="text-3xl sm:text-4xl font-bold text-center mb-8 text-transparent bg-clip-text bg-gradient-to-r from-purple-600 via-indigo-600 to-blue-600">
          Create New Category
        </h2>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-1">
              Category Name
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={category.name}
              onChange={handleChange}
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
              required
            />
          </div>

          

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <textarea
              id="description"
              name="description"
              value={category.description}
              onChange={handleChange}
              rows="3"
              className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
            />
          </div>

          <div>
            <label htmlFor="image" className="block text-sm font-medium text-gray-700 mb-1">
              Image
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="file"
                id="image"
                onChange={handleCategoryImageChange}
                className="hidden"
                accept="image/*"
              />
              <label
                htmlFor="image"
                className="cursor-pointer bg-white px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <Upload className="w-5 h-5 inline-block mr-2" />
                Choose file
              </label>
              <span className="text-sm text-gray-500">
                {category.image ? "Image uploaded" : 'No file chosen'}
              </span>
            </div>
          </div>

          <div className="space-y-6">
            <h3 className="text-xl font-semibold text-center mb-4 text-indigo-700">
              Create Subcategories (Optional)
            </h3>
            {category.subcategories.map((subcategory, index) => (
              <div key={index} className="bg-gray-50 p-6 rounded-lg relative">
                <button
                  type="button"
                  onClick={() => removeSubcategory(index)}
                  className="absolute top-4 right-4 text-gray-400 hover:text-red-500"
                >
                  <X className="w-5 h-5" />
                </button>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Subcategory Name
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={subcategory.name}
                      onChange={(e) => handleSubcategoryChange(index, e)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                      required
                    />
                  </div>

                

                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Description
                    </label>
                    <textarea
                      name="description"
                      value={subcategory.description}
                      onChange={(e) => handleSubcategoryChange(index, e)}
                      rows="2"
                      className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                    />
                  </div>

                 
                </div>
              </div>
            ))}

            <button
              type="button"
              onClick={addSubcategory}
              className="w-full py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 flex items-center justify-center"
            >
              <Plus className="w-5 h-5 mr-2" />
              Add Subcategory
            </button>
          </div>

          <div className="flex justify-end space-x-4">
            <button
              type="button"
              onClick={() => navigate('/admin/ListCategories')}
              className="px-6 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Cancel
            </button>
            <button
              type="submit"
              className="px-6 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
            >
              Create Category
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  );
};

export default CreateCategory;
